import { useState, useEffect } from 'react';
import { X, AlertTriangle, Flag, CheckCircle } from 'lucide-react';
import { apiUrl } from '../../utils/api';

const REASON_OPTIONS = [
  { value: 'unfulfilled', label: 'No cumplió lo prometido' },
  { value: 'inappropriate', label: 'Contenido inapropiado' },
  { value: 'fraud', label: 'Sospecha de fraude' },
  { value: 'other', label: 'Otro' },
];

interface Props {
  isOpen: boolean;
  campaignId: string;
  onClose: () => void;
}

export function ReportCampaignModal({ isOpen, campaignId, onClose }: Props) {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [alreadyReported, setAlreadyReported] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user already reported this campaign
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSuccess(false);
    setError('');
    setReason('');
    setComment('');

    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    fetch(apiUrl(`/api/campaigns/${campaignId}/report/status`), {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(json => {
        if (json.success) setAlreadyReported(json.data.hasReported);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, campaignId]);

  if (!isOpen) return null;

  const canSubmit = reason && comment.trim().length >= 20;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const res = await fetch(apiUrl(`/api/campaigns/${campaignId}/report`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ reason, comment: comment.trim() }),
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error?.message || 'Error al enviar el reporte');
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center space-x-2">
              <Flag className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900">Reportar Campaña</h2>
            </div>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            {loading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
            )}

            {!loading && alreadyReported && !success && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-amber-900">Ya reportaste esta campaña</p>
                <p className="text-xs text-amber-700 mt-1">
                  Nuestro equipo revisará tu reporte. Si el problema persiste, puedes contactarnos directamente.
                </p>
              </div>
            )}

            {!loading && success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-green-900">Reporte enviado</p>
                <p className="text-xs text-green-700 mt-1">
                  Nuestro equipo lo revisará. Gracias por ayudar a mantener la confianza en la plataforma.
                </p>
              </div>
            )}

            {!loading && !alreadyReported && !success && (
              <>
                <p className="text-sm text-gray-600">
                  Si consideras que esta campaña no es legítima o tiene algún problema, puedes reportarla. Nuestro equipo la revisará.
                </p>

                {/* Reason select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo del reporte <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => { setReason(e.target.value); if (error) setError(''); }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Selecciona un motivo</option>
                    {REASON_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Describe el problema <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => { setComment(e.target.value); if (error) setError(''); }}
                    rows={4}
                    maxLength={1000}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Explica por qué reportas esta campaña (mínimo 20 caracteres)..."
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{comment.length}/1000</p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {success || alreadyReported ? 'Cerrar' : 'Cancelar'}
            </button>
            {!loading && !alreadyReported && !success && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar reporte'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
